import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateBatteryPrice } from "./battery-price-dependents"
import type {
  BatteryProposalFinancingDto,
  SaveBatteryConcertOfferDto,
} from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

/**
 * Save a returned Concert Battery offer. Like Save & Calculate Payment it
 * moves the pricing-input generation, so both outcomes refresh everything
 * downstream of the stored price — a failed save leaves the retained Quote
 * stale rather than current.
 */
export default function useSaveBatteryConcertOffer(): UseMutationResult<
  BatteryProposalFinancingDto,
  unknown,
  { proposalId: string; body: SaveBatteryConcertOfferDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }) =>
      batteryProposalService.saveConcertOffer(proposalId, body),
    onError: (_error, { proposalId }) => {
      invalidateBatteryPrice(queryClient, proposalId)
    },
    onSuccess: (financing, { proposalId }) => {
      // The response is the financing read after the offer was saved.
      queryClient.setQueryData(
        queryKeys.batteryProposal.financing(proposalId),
        financing,
      )
      invalidateBatteryPrice(queryClient, proposalId)
    },
  })
}
